import { CHAT_API_URL } from './config'

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

export interface ChatResponse {
  response: string
}

// Built-in responses for when the backend is not available
const OFFLINE_RESPONSES: { pattern: RegExp; response: string }[] = [
  {
    pattern: /w-?2/i,
    response:
      'A W-2 is a tax form your employer sends you each year. It shows your total wages earned and the taxes withheld (federal, state, Social Security, and Medicare). You need it to file your tax return. In Ameritax, go to Income > Wages (W2) to enter your W-2 information.'
  },
  {
    pattern: /standard.*deduction|deduction.*standard|itemiz/i,
    response:
      'The standard deduction is a fixed amount you can subtract from your income. For 2024, it\'s $14,600 for single filers and $29,200 for married filing jointly. Itemized deductions let you list specific expenses (mortgage interest, state taxes, charitable donations). Choose whichever gives you a larger deduction. Most taxpayers benefit from the standard deduction.'
  },
  {
    pattern: /credit|child.*tax|earned.*income|eitc|ctc/i,
    response:
      'Tax credits directly reduce the tax you owe (more valuable than deductions). Common credits include: Child Tax Credit (up to $2,000 per child under 17), Earned Income Tax Credit (for low-to-moderate income workers), and the Additional Child Tax Credit. Ameritax automatically calculates eligible credits based on your information.'
  },
  {
    pattern: /deduct|what.*can.*i/i,
    response:
      'Common tax deductions include: student loan interest (up to $2,500), mortgage interest, state and local taxes (up to $10,000), charitable contributions, medical expenses (above 7.5% of AGI), and HSA contributions. In Ameritax, navigate to the Deductions section to enter these items.'
  },
  {
    pattern: /1099/i,
    response:
      'A 1099 is a tax form reporting income other than wages. Common types: 1099-INT (bank interest), 1099-DIV (dividends), 1099-B (investment sales), 1099-R (retirement distributions), and 1099-NEC (freelance income). Enter your 1099s in Income > Income (1099) in Ameritax.'
  },
  {
    pattern: /refund|owe|how much/i,
    response:
      'Your refund or amount owed depends on the difference between taxes withheld and your actual tax liability. If your employer withheld more than you owe, you get a refund. Complete all sections in Ameritax, and check the refund tracker in the header for an estimate.'
  },
  {
    pattern: /hsa|health.*savings/i,
    response:
      'A Health Savings Account (HSA) lets you save pre-tax money for medical expenses. Contributions are tax-deductible, growth is tax-free, and withdrawals for qualified medical expenses are tax-free. For 2024, the contribution limit is $4,150 for individual coverage and $8,300 for family coverage.'
  },
  {
    pattern: /ira|retirement|401/i,
    response:
      'Traditional IRA contributions may be tax-deductible (reducing your taxable income). Roth IRA contributions are not deductible, but qualified withdrawals are tax-free. The 2024 contribution limit is $7,000 ($8,000 if 50+). Go to Savings Accounts > IRA in Ameritax to enter your contributions.'
  },
  {
    pattern: /file|deadline|when/i,
    response:
      'The federal tax filing deadline is typically April 15. You can request an automatic 6-month extension using Form 4868, but you still need to pay estimated taxes owed by April 15 to avoid penalties. Ameritax helps you prepare your return for filing.'
  },
  {
    pattern: /ameritax|how.*use|help|start/i,
    response:
      'Welcome to Ameritax! Here\'s how to get started: 1) Enter your personal information (name, SSN, address, filing status). 2) Add your income documents (W-2s, 1099s). 3) Enter any deductions and credits. 4) Review your return and generate PDF forms. The sidebar guides you through each step.'
  }
]

const DEFAULT_RESPONSE =
  "I'm your Ameritax tax assistant. I can help with questions about W-2s, 1099s, deductions, credits, filing status, and how to use Ameritax. What would you like to know?"

const getOfflineResponse = (message: string): string => {
  for (const entry of OFFLINE_RESPONSES) {
    if (entry.pattern.test(message)) {
      return entry.response
    }
  }
  return DEFAULT_RESPONSE
}

export const sendChatMessage = async (
  messages: ChatMessage[]
): Promise<string> => {
  // Try the API first
  try {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 5000)

    const response = await fetch(CHAT_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages }),
      signal: controller.signal
    })

    clearTimeout(timeout)

    if (!response.ok) {
      throw new Error(`Chat request failed: ${response.statusText}`)
    }

    const data = (await response.json()) as ChatResponse
    return data.response
  } catch {
    // Backend not available - use built-in responses
    const lastUserMessage = [...messages]
      .reverse()
      .find((m) => m.role === 'user')
    return getOfflineResponse(lastUserMessage?.content ?? '')
  }
}
